import apiClient from "./api";

/** Read-only tenant view of headless agent jobs (WP-42) — webhook runs, scheduled and portal-run workflows. */
export type AgentJobStatus =
  | "Planned"
  | "Running"
  | "WaitingForApproval"
  | "WaitingForInput"
  | "Paused"
  | "Completed"
  | "Failed"
  | "Cancelled";

export type AgentJobTrigger = "Chat" | "Webhook" | "Schedule" | "Portal";

export const AGENT_JOB_STATUSES: AgentJobStatus[] = [
  "Running",
  "WaitingForApproval",
  "WaitingForInput",
  "Completed",
  "Failed",
  "Cancelled",
];

/** The triggers the page filters by — headless only; the list never contains chat turns. */
export const AGENT_JOB_TRIGGERS: AgentJobTrigger[] = [
  "Webhook",
  "Schedule",
  "Portal",
];

export interface AgentJobApproval {
  id: string;
  stepId: string;
  stepLabel: string | null;
  toolName: string;
  riskLevel: string;
  isDestructive: boolean;
  status: "Pending" | "Approved" | "Denied" | "Expired";
  requestedAt: string;
  expiresAt: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  isToolCall: boolean;
  /** For a sub-agent's grant: the records the step will act on, as its plan named them upstream (WP-51). */
  targets: AgentJobApprovalTarget[];
}

export interface AgentJobApprovalTarget {
  from: string;
  field: string;
  ids: string[];
}

export interface AgentJobCard {
  id: string;
  chatSessionId: string | null;
  title: string;
  summary: string | null;
  trigger: AgentJobTrigger;
  status: AgentJobStatus;
  statusReason: string | null;
  action: "approve" | "decide" | null;
  pendingApprovals: AgentJobApproval[];
  clarificationStepId: string | null;
  questions: string[];
  ownerCustomerId: string | null;
  costUsd: number;
  /** What launched the run (WP-70): 0 chat, 1 workflow, 2 autonomous agent, 3 portal; null on older rows. */
  sourceKind: number | null;
  /** The workflow or autonomous agent behind the run, when one did. */
  sourceId: string | null;
  inputTokens: number;
  outputTokens: number;
  createdAt: string;
  updatedAt: string;
}

export interface AgentJobStep {
  id: string;
  kind: string;
  label: string;
  status: string;
  attempt: number;
  maxAttempts: number;
  iteration: number;
  /** A loop step's iteration budget (WP-53); null on any other kind. */
  maxIterations: number | null;
  lastError: string | null;
  dependsOn: string[];
  startedAt: string | null;
  completedAt: string | null;
  questions: string[];
}

export interface AgentJobEvent {
  jobId: string;
  sessionId: string | null;
  sequence: number;
  stepId: string | null;
  type: string;
  payload: unknown;
  at: string;
}

export interface AgentJobDetail {
  job: AgentJobCard;
  steps: AgentJobStep[];
  approvals: AgentJobApproval[];
  events: AgentJobEvent[];
  output: string | null;
}

export interface AgentJobPage {
  items: AgentJobCard[];
  total: number;
  page: number;
  pageSize: number;
}

export const agentJobsService = {
  list: async (
    page: number,
    pageSize: number,
    status?: string,
    trigger?: string,
  ): Promise<AgentJobPage> => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (status) params.set("status", status);
    if (trigger) params.set("trigger", trigger);
    const response = await apiClient.get(`/AgentJobs?${params}`);
    return response.data;
  },

  get: async (jobId: string): Promise<AgentJobDetail> => {
    const response = await apiClient.get(`/AgentJobs/${jobId}`);
    return response.data;
  },
};
